import { isAxiosError } from 'axios';
import type { ApiError } from '@/types/api';

export interface ExtractedError {
  /** Human-readable top-level message */
  message: string;
  /** Error code (e.g. VALIDATION_ERROR, NOT_FOUND) */
  code?: string;
  /** Per-field validation errors, keyed by field name */
  fieldErrors: Record<string, string>;
  /** HTTP status code */
  status?: number;
}

/**
 * Extracts a structured error from an unknown error value.
 * Handles Axios errors with the project's standard API error envelope:
 * { error: { code, message, details: [{ field, message }] } }
 */
export function extractApiError(error: unknown, fallback = 'Something went wrong'): ExtractedError {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const apiError = (error.response?.data as { error?: ApiError } | undefined)?.error;

    if (apiError) {
      const fieldErrors: Record<string, string> = {};
      if (apiError.details && Array.isArray(apiError.details)) {
        for (const detail of apiError.details) {
          if (detail.field) {
            fieldErrors[detail.field] = detail.message;
          }
        }
      }

      return {
        message: apiError.message || fallback,
        code: apiError.code,
        fieldErrors,
        status,
      };
    }

    // No structured error in response — use axios message or status text
    return {
      message: error.message || fallback,
      fieldErrors: {},
      status,
    };
  }

  if (error instanceof Error) {
    return { message: error.message, fieldErrors: {} };
  }

  return { message: fallback, fieldErrors: {} };
}

/**
 * Returns a single-line message suitable for a toast.
 * If there are field errors, appends the first one for clarity.
 */
export function toToastMessage(error: unknown, fallback = 'Something went wrong'): string {
  const extracted = extractApiError(error, fallback);
  const firstField = Object.entries(extracted.fieldErrors)[0];
  if (firstField) {
    return `${extracted.message}: ${firstField[0]} — ${firstField[1]}`;
  }
  return extracted.message;
}
